import {
  createListenerMiddleware,
  createSelector,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit"
import {
  PracticeContact,
  PracticeDetails,
  Practice,
  PracticeAddress,
  TaxInformation,
  AuthAndReleases,
  LiabilityInsurance,
  Location,
  PracticeAgreement,
  TaxInfoTables,
  LocationPhoto,
  VirtualLocation,
  PracticeInvite,
  PracticeCompleteness,
  PracticeAffiliate,
} from "./schema"
import { RootState } from "../../reduxStore/store"
import { hasCaliforniaAddressOrLocation, syncLocationPhotos } from "./helpers"
import { tinValidationStatuses } from "../TinValidation/schema"
import { getPracticeCompletenessData } from "./loaders/practiceCompleteness"
import { TaxInfo } from "../Practices/schema"
import { PracticeTypeDisplayId } from "../enums/displayId.enum"
import {
  isPracticeStepEffectiveComplete,
  isPracticeStepEffectiveCompleteV2,
  isTaxInformationComplete,
} from "../ViewModel/helpers/practiceHelpers"
import { posthog } from "../../posthogInstance"
import { FeatureFlag } from "../enums/featureFlags"

export interface PracticeState {
  practice: Practice
  practiceCompleteness: PracticeCompleteness
  taxInfoTables: TaxInfoTables
  selectedLocation: VirtualLocation | Location | null
}

export const initialPracticeAddress: PracticeAddress = {
  addressOne: "",
  addressTwo: "",
  city: "",
  county: "",
  stateOrTerritory: "",
  zipCode: "",
}

export const initialPracticeContact: PracticeContact = {
  phones: [
    {
      id: self.crypto.randomUUID(),
      type: null,
      value: "",
      isPrimary: true,
      error: false,
    },
  ],
  primaryEmail: "",
  altEmails: [
    {
      id: self.crypto.randomUUID(),
      type: null,
      value: "",
      isPrimary: false,
      error: false,
    },
  ],
  faxes: [
    {
      id: self.crypto.randomUUID(),
      type: null,
      value: "",
      isPrimary: true,
      error: false,
    },
  ],
}

export const initialPracticeDetails: PracticeDetails = {
  logoFile: undefined,
}

export const initialTaxInfo: TaxInfo = {
  id: "",
  tinValidationStatus: "pending",
  governmentTaxId: "",
  governmentTaxType: "us-ein",
  legalName: "",
  alternateTaxName: "",
  fileId: "",
  fileName: "",
  createdBy: "",
  createdDateTime: "",
  lastUpdatedBy: "",
  lastUpdatedDateTime: "",
}

export const initialTaxInformation: TaxInformation = {
  id: "",
  governmentTaxIdType: "us-ein",
  governmentTaxId: "",
  taxOrganizationType: undefined,
  legalName: "",
  altName: "",
  file: undefined,
  fileUploadDate: null,
  fileUploadedBy: null,
  taxVerified: false,
  tinValidationStatus: tinValidationStatuses.PENDING,
}

export const initialAuthAndReleases: AuthAndReleases = {
  signedFileId: null,
  signedOn: null,
  signedBy: null,
  signingUrl: "",
}

export const initialPracticeAgreement: PracticeAgreement = {
  practiceAgreement: {
    signedFileId: null,
    signedOn: null,
    signedBy: null,
    fileStatusDisplayId: null,
    resetOn: null,
  },
  knoxKeeneAgreement: {
    signedFileId: null,
    signedOn: null,
    signedBy: null,
    fileStatusDisplayId: null,
    resetOn: null,
  },
  redirectUrl: "",
}

const intialTaxTables = {
  taxOrganizationTypes: [],
  governmentTaxTypes: [],
}

const intialLocationPhotos: LocationPhoto[] = []

const initialLiabilityInsurances: LiabilityInsurance[] = []

const initialLocations: Location[] = []
const initialPracticeInvites: PracticeInvite[] = []
const initialPracticeAffiliates: PracticeAffiliate[] = []

// this is used for location create flow as a placeholder until the user saves
export const placeholderSelectedLocation: Location = {
  id: null,
  line1: null,
  line2: null,
  city: null,
  stateOrTerritory: null,
  postalCode: null,
  county: null,
  isPrimary: false,
  locationTypeDisplayId: null,
  fax: null,
  isAdaAccessible: false,
  officeRequirementsCertified: false,
  lastUpdated: null,
  phones: [],
  emails: [],
}

export const initialPractice: Practice = {
  id: "",
  name: "",
  status: "pending", // JPF: should we use undefined here for base state? Pending is enabling some auth flows when we have no practice at all. TODO: #8823
  isVisible: false,
  websiteUrl: undefined,
  overview: undefined,
  logoImageUrl: undefined,
  deleteDateTime: undefined,
  createdDateTime: "",
  createdBy: "",
  lastUpdatedDateTime: "",
  authReleaseRequired: null,
  hasOnlineServicesInCalifornia: null,
  hasNoPhysicalLocations: null,
  isKnoxKeeneStale: null,
  isPracticeAgreementStale: null,
  tags: [],
  taxInfo: initialTaxInfo,
  taxInformation: initialTaxInformation,
  practiceInfo: {
    practiceAddress: initialPracticeAddress,
    practiceContact: initialPracticeContact,
    practiceDetails: initialPracticeDetails,
  },
  providersInfo: [],
  authAndReleases: initialAuthAndReleases,
  practiceAffiliates: initialPracticeAffiliates,
  billingAddress: null,
  liabilityInsurances: initialLiabilityInsurances,
  locations: initialLocations,
  locationPhotos: intialLocationPhotos,
  virtualLocation: null,
  practiceAgreement: initialPracticeAgreement,
  practiceInvites: initialPracticeInvites,
  legacyAffiliateId: null,
  practiceTypeDisplayId: PracticeTypeDisplayId.STANDARD,
}

const initialPracticeCompleteness: PracticeCompleteness = {
  isPracticeInformationComplete: false,
  isPracticeTaxInformationComplete: false,
  isPracticeAuthReleaseComplete: false,
  isPracticeUsingLiabilityInsurance: false,
  isPracticeLiabilityInformationComplete: false,
  isPracticeLocationComplete: false,
  isPracticeAgreementComplete: false,
}

const initialState: PracticeState = {
  practice: initialPractice,
  practiceCompleteness: initialPracticeCompleteness,
  taxInfoTables: intialTaxTables,
  selectedLocation: null,
}

export const practiceSlice = createSlice({
  name: "practice",
  initialState,
  reducers: {
    practiceUpdated: (
      state: PracticeState,
      { payload }: PayloadAction<Practice>,
    ) => {
      const { updatedPractice } = syncLocationPhotos(
        payload,
        state.selectedLocation,
      )
      // We could technically keep chaining sync calls here as needed
      state.practice = updatedPractice
    },
    taxInfoTablesUpdated: (
      state: PracticeState,
      { payload }: PayloadAction<TaxInfoTables>,
    ) => {
      state.taxInfoTables = {
        taxOrganizationTypes:
          payload.taxOrganizationTypes !== undefined
            ? payload.taxOrganizationTypes
            : state.taxInfoTables.taxOrganizationTypes,

        governmentTaxTypes:
          payload.governmentTaxTypes !== undefined
            ? payload.governmentTaxTypes
            : state.taxInfoTables.governmentTaxTypes,
      }
    },
    patchPractice: (
      state: PracticeState,
      { payload }: PayloadAction<Partial<Practice>>,
    ) => {
      state.practice = {
        ...state.practice,
        ...payload,
      }
    },
    updatePracticeCompleteness: (
      state: PracticeState,
      { payload }: PayloadAction<PracticeCompleteness>,
    ) => {
      state.practiceCompleteness = {
        ...state.practiceCompleteness,
        ...payload,
      }
    },
    updateSelectedLocation: (
      state: PracticeState,
      { payload }: PayloadAction<VirtualLocation | Location | null>,
    ) => {
      // Clear location photos when switching to a different location to prevent stale data
      if (payload?.id !== state.selectedLocation?.id) {
        state.practice.locationPhotos = []
      }
      state.selectedLocation = payload
    },
  },
})

// Export actions and reducer
export const {
  practiceUpdated,
  updatePracticeCompleteness,
  taxInfoTablesUpdated,
  updateSelectedLocation,
  patchPractice,
} = practiceSlice.actions

const listenerMiddleware = createListenerMiddleware()

listenerMiddleware.startListening({
  actionCreator: practiceUpdated,
  effect: async (action, listenerApi) => {
    const practiceId = action.payload.id
    listenerApi.dispatch(getPracticeCompletenessData(practiceId))
  },
})

export { listenerMiddleware }

export const selectPractice = (state: {
  practice: PracticeState
}): Practice => {
  return state.practice.practice
}

export const selectPracticeCompleteness = (
  state: RootState,
): PracticeCompleteness => {
  return state.practice.practiceCompleteness
}

export const selectIsPracticeComplete = (authReleaseRequired?: boolean) =>
  createSelector(
    [selectPracticeCompleteness, selectPractice],
    (
      {
        isPracticeInformationComplete,
        isPracticeAuthReleaseComplete,
        isPracticeUsingLiabilityInsurance,
        isPracticeLiabilityInformationComplete,
        isPracticeLocationComplete,
        isPracticeAgreementComplete,
      },
      practice,
    ) => {
      // Override practice tax info to allow for invalid tin/missing W9
      // see https://dev.azure.com/HACloud/VEAP/_git/affiliate-web/pullrequest/7218
      // and https://dev.azure.com/HACloud/VEAP/_git/affiliate-web/pullrequest/16994
      const isTaxComplete = isTaxInformationComplete(practice.taxInformation)
      const isLiabilityComplete = isPracticeUsingLiabilityInsurance
        ? isPracticeLiabilityInformationComplete
        : true
      const isAuthReleaseComplete =
        authReleaseRequired === undefined
          ? isPracticeAuthReleaseComplete
          : !authReleaseRequired || isPracticeAuthReleaseComplete
      // Allow stale agreements for completeness check (see bug: https://dev.azure.com/HACloud/VEAP/_workitems/edit/11656)
      const effectiveComplete = posthog.isFeatureEnabled(
        FeatureFlag.IsResetRemoveAgreements20047Enabled,
      )
        ? isPracticeStepEffectiveCompleteV2(practice)
        : isPracticeStepEffectiveComplete(practice)
      const practiceStepComplete =
        isPracticeAgreementComplete || effectiveComplete
      return (
        isPracticeInformationComplete &&
        isTaxComplete &&
        isAuthReleaseComplete &&
        isLiabilityComplete &&
        isPracticeLocationComplete &&
        practiceStepComplete
      )
    },
  )

export const selectTaxInfoTables = (state: {
  practice: PracticeState
}): TaxInfoTables => {
  return state.practice.taxInfoTables
}

export const selectSelectedLocation = (
  state: RootState,
): VirtualLocation | Location | null => {
  return state.practice.selectedLocation
}

// Knox keene required if selectCaliforniaLawsApply === true
// Practice agreement required if practice.hasOnlineServicesInCalifornia === false
export const selectCalifoniaLawsApply = createSelector(
  [selectPractice],
  (practice) => {
    const hasOnlineServicesInCalifornia = practice.hasOnlineServicesInCalifornia
    const hasCaliforniaAddress = hasCaliforniaAddressOrLocation(practice)

    return hasOnlineServicesInCalifornia || hasCaliforniaAddress
  },
)

export default practiceSlice.reducer
